import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { accountLedgerService } from '../../services/accountLedger/accountLedgerService'
import { showSuccessToast, showErrorToast, handleApiError } from '../../utils/errorHandler'

export const useAccountLedgerEntries = (params?: { from?: string; to?: string; page?: number; account?: string; bank_account_id?: string }) => {
  return useQuery({
    queryKey: ['account-ledger', params],
    queryFn: () => accountLedgerService.getEntries(params ?? {}),
    refetchOnMount: 'always',
  })
}

export const useAccountSummary = () => useQuery({
  queryKey: ['account-summary'],
  queryFn: () => accountLedgerService.getSummary(),
  refetchOnMount: 'always',
})

export const useCashCloses = () => useQuery({
  queryKey: ['cash-closes'],
  queryFn: () => accountLedgerService.getCloses(),
  refetchOnMount: 'always',
})

const useLedgerMutation = (message: string) => {
  const queryClient = useQueryClient()
  return { onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['account-ledger'] })
    queryClient.invalidateQueries({ queryKey: ['account-ledger-balance'] })
    queryClient.invalidateQueries({ queryKey: ['account-summary'] })
    queryClient.invalidateQueries({ queryKey: ['cash-closes'] })
    showSuccessToast(message)
  }, onError: (error: unknown) => showErrorToast(handleApiError(error)) }
}

export const useAddBankAccount = () => useMutation({
  mutationFn: (name: string) => accountLedgerService.addBank(name),
  ...useLedgerMutation('Bank account added'),
})

export const useReconcileOpening = () => useMutation({
  mutationFn: accountLedgerService.reconcileOpening,
  ...useLedgerMutation('Opening balances confirmed'),
})

export const useCloseCashDay = () => useMutation({
  mutationFn: accountLedgerService.closeDay,
  ...useLedgerMutation('Cash day closed'),
})

export const useRecordBankDeposit = () => useMutation({
  mutationFn: accountLedgerService.deposit,
  ...useLedgerMutation('Bank deposit recorded'),
})

export const useCurrentBalance = () => {
  return useQuery({
    queryKey: ['account-ledger-balance'],
    queryFn: () => accountLedgerService.getCurrentBalance(),
    refetchOnMount: 'always',
  })
}

export const useSetOpeningBalance = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (amount: number) => accountLedgerService.setOpeningBalance(amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-ledger'] })
      queryClient.invalidateQueries({ queryKey: ['account-ledger-balance'] })
      queryClient.invalidateQueries({ queryKey: ['account-summary'] })
      showSuccessToast('Opening balance set successfully')
    },
    onError: (error) => showErrorToast(error),
  })
}
